import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { LogOut, RefreshCw } from "lucide-react";
import PasscodeGate from "../components/launch/PasscodeGate";
import DailyChart from "../components/launch/DailyChart";
import { ButtonsTable, SourcesTable } from "../components/launch/StatsTables";
import LinkBuilder from "../components/launch/LinkBuilder";
import BudgetPanel from "../components/launch/BudgetPanel";
import { ExportPanel, WeeklyEmailCard } from "../components/launch/ReportTools";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "cdva_launch_stats_token";
const RANGES = [7, 30, 90];

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E6E1D8] p-5" data-testid={testid}>
      <div className="text-[12px] uppercase tracking-[0.12em] text-[#5B6B7B] font-semibold">{label}</div>
      <div className="font-display font-extrabold text-[34px] text-[#1A2A3A] tracking-[-0.02em] mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-[13px] text-[#5B6B7B] mt-0.5">{sub}</div>}
    </div>
  );
}

function Overview({ t, devices }) {
  const dev = (k) => `${devices.desktop[k]} desktop · ${devices.mobile[k]} mobile`;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Kpi testid="kpi-visits" label="Visits" value={t.visit} sub={dev("visit")} />
      <Kpi testid="kpi-get-started" label="Get started clicks" value={t.get_started} sub={`${t.get_started_rate}% of visits · ${dev("get_started")}`} />
      <Kpi testid="kpi-app-store" label="App Store clicks" value={t.app_store} sub={`${t.app_store_rate}% of visits · ${dev("app_store")}`} />
    </div>
  );
}

export default function LaunchStats() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const signOut = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setData(null);
  }, []);

  const load = useCallback(async () => {
    setError("");
    const r = await fetch(`${API}/api/marketing/stats?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.status === 401) return signOut();
    if (!r.ok) return setError("Could not load statistics. Please try again.");
    setData(await r.json());
  }, [days, token, signOut]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const unlock = (tk) => {
    sessionStorage.setItem(TOKEN_KEY, tk);
    setToken(tk);
  };

  return (
    <>
      <Helmet>
        <title>Launch statistics · ClientDesk VA</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {!token ? (
        <PasscodeGate onUnlock={unlock} />
      ) : (
        <div className="min-h-screen bg-[#FAF7F2] text-[#1A2A3A]" data-testid="launch-stats-dashboard">
          <header className="bg-[#22384F] text-white">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center gap-4 justify-between">
              <div>
                <div className="text-[12px] uppercase tracking-[0.16em] text-[#F0B67F] font-semibold">Private</div>
                <h1 className="font-display font-extrabold text-[26px] sm:text-[30px] tracking-[-0.02em]">Launch statistics</h1>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex bg-white/10 rounded-full p-1" role="tablist">
                  {RANGES.map((d) => (
                    <button
                      key={d}
                      onClick={() => setDays(d)}
                      data-testid={`launch-stats-range-${d}`}
                      aria-pressed={days === d}
                      className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${days === d ? "bg-white text-[#22384F]" : "text-white/80 hover:text-white"}`}
                    >
                      {d} days
                    </button>
                  ))}
                </div>
                <button onClick={load} aria-label="Refresh" data-testid="launch-stats-refresh" className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                  <RefreshCw size={16} />
                </button>
                <button onClick={signOut} aria-label="Lock" data-testid="launch-stats-signout" className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          </header>
          <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 space-y-6">
            {error && <p className="text-[#B42318]" role="alert" data-testid="launch-stats-error">{error}</p>}
            {!data ? (
              <p className="text-[#5B6B7B]" data-testid="launch-stats-loading">Loading…</p>
            ) : (
              <>
                <Overview t={data.totals} devices={data.devices} />
                <DailyChart data={data.daily} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2"><SourcesTable rows={data.sources} /></div>
                  <ButtonsTable rows={data.buttons} />
                </div>
                <LinkBuilder token={token} />
                <BudgetPanel token={token} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ExportPanel token={token} />
                  <WeeklyEmailCard token={token} />
                </div>
                <p className="text-[12px] text-[#5B6B7B] leading-relaxed">
                  Visits are counted once per browser tab session. Clicks are tagged with the ad link's utm_source and utm_campaign. Visitors with Do Not Track or Global Privacy Control turned on aren't counted. No cookies, IP addresses or personal details are stored. App Store clicks show interest; confirmed downloads are in App Store Connect.
                </p>
              </>
            )}
          </main>
        </div>
      )}
    </>
  );
}
